import { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/feature/AppShell";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { fetchCurrentProfile } from "@/services/auth";
import {
  addComment,
  createPost,
  deleteComment,
  deletePost,
  fetchGroups,
  fetchPosts,
  ProhibitedContentError,
  setReaction,
  type CommunityGroup,
  type CommunityPost,
  type FeedFilter,
} from "@/services/community";
import { timeAgo } from "@/lib/timeAgo";
import CourseVideosSection from "./CourseVideosSection";

const MAX_POST = 1500;

export default function Comunidad() {
  const [groups, setGroups] = useState<CommunityGroup[]>([]);
  const [filter, setFilter] = useState<FeedFilter>("all");
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<{ id: string; isAdmin: boolean; name: string | null; avatar: string | null } | null>(null);

  const [draft, setDraft] = useState("");
  const [draftGroup, setDraftGroup] = useState<string>("");
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [openComments, setOpenComments] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commenting, setCommenting] = useState(false);

  const showNotice = (msg: string) => {
    setNotice(msg);
    window.setTimeout(() => setNotice(null), 3500);
  };

  const prohibitedMessage = (err: ProhibitedContentError) =>
    `No se puede publicar: incluye ${err.terms.map((t) => `"${t}"`).join(", ")}, que las reglas de tu equipo no permiten. Reescríbelo y vuelve a intentar.`;

  const loadPosts = useCallback(async (f: FeedFilter) => {
    try {
      setPosts(await fetchPosts(f));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar la comunidad.");
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([fetchCurrentProfile(), fetchGroups()])
      .then(([profile, g]) => {
        if (!active) return;
        setGroups(g);
        if (profile) setMe({ id: profile.id, isAdmin: profile.role === "admin", name: profile.fullName, avatar: profile.avatarUrl });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchPosts(filter)
      .then((data) => {
        if (!active) return;
        setPosts(data);
        setError(null);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudo cargar la comunidad.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filter]);

  // Si el usuario está viendo un grupo, publica ahí por defecto.
  useEffect(() => {
    setDraftGroup(filter !== "all" && filter !== "general" ? filter : "");
  }, [filter]);

  const groupTitle = (id: string | null) => groups.find((g) => g.id === id)?.title ?? null;

  const handlePublish = async () => {
    if (!draft.trim() || publishing) return;
    setPublishing(true);
    try {
      await createPost({ content: draft, groupId: draftGroup || null });
      setDraft("");
      await loadPosts(filter);
    } catch (err) {
      showNotice(
        err instanceof ProhibitedContentError
          ? prohibitedMessage(err)
          : err instanceof Error
            ? err.message
            : "No se pudo publicar."
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleLike = async (post: CommunityPost) => {
    const liked = !post.likedByMe;
    setPosts((prev) =>
      prev.map((p) => (p.id === post.id ? { ...p, likedByMe: liked, likes: p.likes + (liked ? 1 : -1) } : p))
    );
    try {
      await setReaction(post.id, liked);
    } catch {
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, likedByMe: !liked, likes: p.likes + (liked ? -1 : 1) } : p))
      );
      showNotice("No se pudo registrar tu reacción.");
    }
  };

  const handleDeletePost = async (post: CommunityPost) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await deletePost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch {
      showNotice("No se pudo eliminar la publicación.");
    }
  };

  const handleComment = async (post: CommunityPost) => {
    if (!commentDraft.trim() || commenting) return;
    setCommenting(true);
    try {
      await addComment(post.id, commentDraft);
      setCommentDraft("");
      await loadPosts(filter);
    } catch (err) {
      showNotice(
        err instanceof ProhibitedContentError
          ? prohibitedMessage(err)
          : err instanceof Error
            ? err.message
            : "No se pudo comentar."
      );
    } finally {
      setCommenting(false);
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    try {
      await deleteComment(commentId);
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, comments: p.comments.filter((c) => c.id !== commentId) } : p))
      );
    } catch {
      showNotice("No se pudo eliminar el comentario.");
    }
  };

  const chipCls = (active: boolean) =>
    [
      "flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
      active
        ? "bg-primary-500 text-background-950"
        : "bg-background-100 text-foreground-600 border border-background-200 hover:border-primary-400",
    ].join(" ");

  return (
    <AppShell headerVariant="onLight">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-primary-500/15 flex items-center justify-center">
            <i className="ri-community-fill text-2xl text-primary-600"></i>
          </div>
          <div>
            <h1 className="font-heading font-bold text-foreground-950 text-xl">Comunidad</h1>
            <p className="text-xs text-foreground-500">Comparte, aprende y celebra con tu equipo.</p>
          </div>
        </div>

        <CourseVideosSection />

        {/* Grupos */}
        <div className="mt-5 flex gap-2 overflow-x-auto vv-scrollbar-hidden pb-1" role="tablist" aria-label="Grupos">
          <button role="tab" aria-selected={filter === "all"} onClick={() => setFilter("all")} className={chipCls(filter === "all")}>
            <i className="ri-apps-2-line"></i> Todo
          </button>
          <button role="tab" aria-selected={filter === "general"} onClick={() => setFilter("general")} className={chipCls(filter === "general")}>
            <i className="ri-chat-1-line"></i> General
          </button>
          {groups.map((g) => (
            <button key={g.id} role="tab" aria-selected={filter === g.id} onClick={() => setFilter(g.id)} className={chipCls(filter === g.id)}>
              <i className={g.icon}></i> {g.title}
            </button>
          ))}
        </div>
        {filter !== "all" && filter !== "general" ? (
          <p className="mt-2 text-xs text-foreground-500">{groups.find((g) => g.id === filter)?.description}</p>
        ) : null}

        {/* Compositor */}
        <div className="mt-5 bg-background-100 border border-background-200 rounded-2xl p-4">
          <div className="flex gap-3">
            <MemberAvatar name={me?.name ?? "Tú"} src={me?.avatar ?? null} />
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, MAX_POST))}
              rows={3}
              placeholder="¿Qué quieres compartir con tu equipo?"
              className="flex-1 bg-transparent outline-none text-sm text-foreground-950 placeholder:text-foreground-400 resize-none"
            />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <select
              value={draftGroup}
              onChange={(e) => setDraftGroup(e.target.value)}
              aria-label="Publicar en"
              className="bg-background-50 border border-background-300 rounded-full px-3 py-1.5 text-xs font-semibold text-foreground-800 cursor-pointer outline-none"
            >
              <option value="">General</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-400">
                {draft.length}/{MAX_POST}
              </span>
              <button
                onClick={handlePublish}
                disabled={!draft.trim() || publishing}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {publishing ? "Publicando…" : "Publicar"}
              </button>
            </div>
          </div>
        </div>

        {notice ? (
          <div role="alert" className="mt-3 bg-accent-500/10 border border-accent-500/30 text-accent-700 text-sm rounded-xl px-4 py-3">
            {notice}
          </div>
        ) : null}

        {/* Feed */}
        <div className="mt-5 flex flex-col gap-4">
          {loading ? (
            <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
              <p className="mt-2 text-sm">Cargando publicaciones…</p>
            </div>
          ) : error ? (
            <div className="bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700 text-sm">{error}</div>
          ) : posts.length === 0 ? (
            <div className="bg-background-100 border border-dashed border-background-300 rounded-2xl p-10 text-center text-foreground-500 text-sm">
              Aún no hay publicaciones aquí. ¡Sé la primera persona en compartir algo!
            </div>
          ) : (
            posts.map((post) => {
              const canDelete = me ? post.authorId === me.id || me.isAdmin : false;
              const expanded = openComments === post.id;
              const gTitle = groupTitle(post.groupId);
              return (
                <article key={post.id} className="bg-background-100 border border-background-200 rounded-2xl p-4">
                  <header className="flex items-start gap-3">
                    <MemberAvatar name={post.authorName} src={post.authorAvatar} />
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-foreground-950 text-sm truncate">{post.authorName}</div>
                      <div className="text-xs text-foreground-500 flex items-center gap-2 flex-wrap">
                        <span>{timeAgo(post.createdAt)}</span>
                        {gTitle ? (
                          <span className="bg-primary-500/10 text-primary-700 rounded-full px-2 py-0.5 font-semibold">{gTitle}</span>
                        ) : null}
                      </div>
                    </div>
                    {canDelete ? (
                      <button
                        onClick={() => handleDeletePost(post)}
                        aria-label="Eliminar publicación"
                        className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-accent-600 hover:bg-background-200 cursor-pointer"
                      >
                        <i className="ri-delete-bin-6-line"></i>
                      </button>
                    ) : null}
                  </header>
                  <p className="mt-3 text-sm text-foreground-900 whitespace-pre-wrap break-words">{post.content}</p>
                  {post.imageUrl ? (
                    <img src={post.imageUrl} alt="" loading="lazy" className="mt-3 w-full rounded-xl border border-background-200 object-cover" />
                  ) : null}
                  <footer className="mt-3 flex items-center gap-4 text-sm">
                    <button
                      onClick={() => handleLike(post)}
                      aria-pressed={post.likedByMe}
                      className={`inline-flex items-center gap-1.5 cursor-pointer font-semibold ${post.likedByMe ? "text-accent-600" : "text-foreground-500 hover:text-accent-600"}`}
                    >
                      <i className={post.likedByMe ? "ri-heart-3-fill" : "ri-heart-3-line"}></i> {post.likes}
                    </button>
                    <button
                      onClick={() => {
                        setOpenComments(expanded ? null : post.id);
                        setCommentDraft("");
                      }}
                      className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-primary-600 cursor-pointer font-semibold"
                    >
                      <i className="ri-chat-3-line"></i> {post.comments.length}
                    </button>
                  </footer>

                  {expanded ? (
                    <div className="mt-3 pt-3 border-t border-background-200 flex flex-col gap-3">
                      {post.comments.map((c) => (
                        <div key={c.id} className="flex items-start gap-2.5">
                          <MemberAvatar name={c.authorName} src={c.authorAvatar} size="sm" />
                          <div className="flex-1 min-w-0 bg-background-50 border border-background-200 rounded-xl px-3 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-bold text-foreground-950 truncate">{c.authorName}</span>
                              <span className="text-[11px] text-foreground-400 whitespace-nowrap">{timeAgo(c.createdAt)}</span>
                            </div>
                            <p className="text-sm text-foreground-800 whitespace-pre-wrap break-words">{c.content}</p>
                          </div>
                          {me && (c.authorId === me.id || me.isAdmin) ? (
                            <button
                              onClick={() => handleDeleteComment(post.id, c.id)}
                              aria-label="Eliminar comentario"
                              className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-accent-600 cursor-pointer"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          ) : null}
                        </div>
                      ))}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleComment(post);
                        }}
                        className="flex items-center gap-2"
                      >
                        <input
                          value={commentDraft}
                          onChange={(e) => setCommentDraft(e.target.value.slice(0, 800))}
                          type="text"
                          placeholder="Escribe un comentario…"
                          className="flex-1 bg-background-50 border border-background-200 rounded-full px-4 py-2 text-sm outline-none focus:border-primary-400"
                        />
                        <button
                          type="submit"
                          disabled={!commentDraft.trim() || commenting}
                          aria-label="Enviar comentario"
                          className="w-9 h-9 rounded-full bg-primary-500 text-background-950 flex items-center justify-center disabled:opacity-50 cursor-pointer"
                        >
                          <i className="ri-send-plane-fill"></i>
                        </button>
                      </form>
                    </div>
                  ) : null}
                </article>
              );
            })
          )}
        </div>
      </div>
    </AppShell>
  );
}
